/**
 * React Query hooks for text prompt groups CRUD
 * For Media Devoted | Created By Ciro Autiero
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';

import api from '@/services/api';

import type {
  TextPromptGroup,
  CreateGroupPayload,
  UpdateGroupPayload,
  ReorderNestedPromptGroupsPayload,
} from '../types/campaign-texts.types';
import { normalizeTextPromptGroup } from '../utils/normalize-text-group';

const GROUPS_KEY = ['campaign-texts', 'groups'] as const;

export function useTextPromptGroups() {
  return useQuery<TextPromptGroup[]>({
    queryKey: [...GROUPS_KEY],
    queryFn: async () => {
      const raw = await api.get('/campaign-texts/groups').then((r) => r.data.data);
      return (raw as TextPromptGroup[]).map((g) => normalizeTextPromptGroup(g));
    },
    staleTime: 30_000,
  });
}

export function useCreateGroup() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: CreateGroupPayload) =>
      api.post('/campaign-texts/groups', payload).then((r) => r.data.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: [...GROUPS_KEY] }),
  });
}

export function useUpdateGroup() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...payload }: UpdateGroupPayload & { id: string }) =>
      api.patch(`/campaign-texts/groups/${id}`, payload).then((r) => r.data.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: [...GROUPS_KEY] }),
  });
}

export function useDeleteGroup() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      api.delete(`/campaign-texts/groups/${id}`).then((r) => r.data.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: [...GROUPS_KEY] }),
  });
}

export function useReorderGroups() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: ReorderNestedPromptGroupsPayload) =>
      api
        .patch('/campaign-texts/groups/reorder', {
          parentId: payload.parentId ?? null,
          orderedIds: payload.orderedIds,
        })
        .then((r) => r.data.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: [...GROUPS_KEY] }),
  });
}
